/**
 * IPQ (Investment Profile Questionnaire) – the four suitability questions.
 * Shared constants and helpers for member-app and grove-app.
 */

import { formatInvestmentProfileValue } from './formatInvestmentProfileValue';
import {
    AccountFluctuationComfortability,
    IncomeSourceStability,
    InvestmentPortfolioLoss,
    InvestmentReturnPreference,
} from './types';

export const IPQ_FIELDS = [
    'incomeSourceStability',
    'investmentPortfolioLoss',
    'investmentReturnPreference',
    'accountFluctuationComfortability',
] as const;

export type IPQField = (typeof IPQ_FIELDS)[number];

/**
 * Exact question text shown to members in the investment questionnaire.
 * Used by member-app (RiskSequence) and grove-app (editable IPQ table).
 */
export const IPQ_QUESTIONS: Array<{ key: IPQField; label: string }> = [
    {
        key: 'incomeSourceStability',
        label: 'My current and future income sources are',
    },
    {
        key: 'investmentPortfolioLoss',
        label: 'If my investment portfolio showed a 15% loss in two months, I would',
    },
    {
        key: 'investmentReturnPreference',
        label: 'I prefer stable investments, even if they may have lower returns',
    },
    {
        key: 'accountFluctuationComfortability',
        label: 'How comfortable are you seeing your account value fluctuate up and down?',
    },
];

const INCOME_SOURCE_STABILITY_VALUES = [
    IncomeSourceStability.VERY_STABLE,
    IncomeSourceStability.STABLE,
    IncomeSourceStability.SOMEWHAT_STABLE,
    IncomeSourceStability.UNSTABLE,
    IncomeSourceStability.VERY_UNSTABLE,
] as const;

const INVESTMENT_PORTFOLIO_LOSS_VALUES = [
    InvestmentPortfolioLoss.BUY_MORE,
    InvestmentPortfolioLoss.HOLD_NOTHING,
    InvestmentPortfolioLoss.SELL_PORTION,
    InvestmentPortfolioLoss.SELL_EVERYTHING,
] as const;

const INVESTMENT_RETURN_PREFERENCE_VALUES = [
    InvestmentReturnPreference.STRONGLY_AGREE,
    InvestmentReturnPreference.AGREE,
    InvestmentReturnPreference.SOMEWHAT_AGREE,
    InvestmentReturnPreference.DISAGREE,
    InvestmentReturnPreference.STRONGLY_DISAGREE,
] as const;

const ACCOUNT_FLUCTUATION_COMFORTABILITY_VALUES = [
    AccountFluctuationComfortability.VERY_COMFORTABLE,
    AccountFluctuationComfortability.COMFORTABLE,
    AccountFluctuationComfortability.SOMEWHAT_COMFORTABLE,
    AccountFluctuationComfortability.UNCOMFORTABLE,
    AccountFluctuationComfortability.VERY_UNCOMFORTABLE,
] as const;

export const IPQ_ENUMS: Record<IPQField, Record<string, string>> = {
    incomeSourceStability: IncomeSourceStability,
    investmentPortfolioLoss: InvestmentPortfolioLoss,
    investmentReturnPreference: InvestmentReturnPreference,
    accountFluctuationComfortability: AccountFluctuationComfortability,
};

export function getIPQOptions(
    key: IPQField,
): Array<{ label: string; value: string | null }> {
    let values: readonly string[];
    switch (key) {
        case 'incomeSourceStability':
            values = INCOME_SOURCE_STABILITY_VALUES;
            break;
        case 'investmentPortfolioLoss':
            values = INVESTMENT_PORTFOLIO_LOSS_VALUES;
            break;
        case 'investmentReturnPreference':
            values = INVESTMENT_RETURN_PREFERENCE_VALUES;
            break;
        case 'accountFluctuationComfortability':
            values = ACCOUNT_FLUCTUATION_COMFORTABILITY_VALUES;
            break;
    }
    return [
        { label: 'Not set', value: null },
        ...values.map((v) => ({
            label: formatInvestmentProfileValue(key, v),
            value: v,
        })),
    ];
}

export function formatIPQValue(
    field: IPQField,
    value: string | null | undefined,
): string {
    if (value == null) return 'Not set';
    return formatInvestmentProfileValue(field, value);
}
